import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { LenderService } from '@shared/service/lender/lender.service';
import { inject, Injectable } from '@angular/core';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { InstaTouchService } from '../../dynamic-app/service/instaTouch/insta-touch.service';
import { ApplicationApiService } from '../../dynamic-app/service/application-api/application-api.service';
import { BundleHelperService } from '../../services/bundle-helper.service';
import { PreciseIDQuestionResponse } from '../../dynamic-app/types/preciseIdQuestionResponse';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { DecisionStatus } from '@workflow/ecp-shared/enum/enum';
import { StatefulValue } from '@workflow/class/stateful-value.class';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { StatefulService } from '@app/service/stateful.service';
import { FieldInstance, FieldService } from '@workflow/class/field.service';
import { CardInstance, CardService } from '@workflow/class/card.service';
import { PageInstance } from '@workflow/class/page.service';
import { FlowInstance } from '@workflow/class/flow.service';
import { StateManagementService } from '@workflow/service/state-management/state-management.service';
import { CapturedConsent } from '@workflow/ecp-shared/model/consent';

const OAO = 'oao';
const APPLICATION_REVIEW_PAGE_ID = 'application-review';
const OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_EMAIL = 'api:oaoMemberInformation/businessInformation/responsibleParties/0/email';
const OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_ISPREFILLED = 'api:oaoMemberInformation/businessInformation/responsibleParties/0/isPrefilled';
const OAO_PRIMARY_APPLICANT_EMAIL = 'api:oaoMemberInformation/primaryApplicant/email';
const OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_MOBILENUMBER =
  'api:oaoMemberInformation/businessInformation/responsibleParties/0/mobileNumber';
const OAO_PRIMARY_APPLICANT_MOBILENUMBER = 'api:oaoMemberInformation/primaryApplicant/mobileNumber';
/**
 * A helper class for all the logics used in page component
 */
@Injectable({
  providedIn: 'root'
})
export class OaoPageHelper {
  private readonly statefulService = inject(StatefulService);

  /** boolean to show remove joint applicant confirmation modal */
  public showDeleteJointApplicantModal: boolean;

  /** Flow instance */
  public get flow(): FlowInstance {
    return this.workflowService.currentFlow;
  }
  /** Page instance */
  public get page(): PageInstance {
    return this.workflowService.currentPage;
  }

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly workflowService: WorkflowService,
    private readonly userService: UserService,
    private readonly applicationApiService: ApplicationApiService,
    private readonly l360Service: L360Service,
    private readonly lenderService: LenderService,
    public readonly instatouchService: InstaTouchService,
    private readonly bundleHelperService: BundleHelperService,
    private readonly fieldService: FieldService,
    private readonly cardService: CardService,
    private readonly stateManagementService: StateManagementService
  ) {
    // Set OAO Membership Dropdown Value
    // const field = this.fieldService.createField(this.MEMBERSHIP_DROPDOWN_ID);
    // if (!field.data.value) {
    //   field.data.value = this.statefulService.wrap(this.MEMBERSHIP_DROPDOWN_BUNDLE_ID).value;
    // }
  }

  public showOaoDeleteModal(): void {
    this.showDeleteJointApplicantModal = true;
  }

  /** Showing Workflow Navigation if oao user registered or logged in */
  public get isUserAuthenticated(): boolean {
    return !!this.userService?.isAuthenticated;
  }

  public oaoNavigationInProgressApp(): void {
    if (this.flow.id.toLowerCase().indexOf('oao') > -1) {
      const currentPageIndex = this.flow.pages.findIndex((t) => t.id === this.page?.id);
      const isFirstOaoNavigation = this.statefulService.wrap('ss:oao-first-time-navigation').value;
      const hasApplicationCheckpointPass = this.statefulService.wrap('api:oaoMemberInformation/hasApplicationCheckpointPass').value;
      const extraData = this.statefulService.wrap('api:extraData').valueString;
      if (isFirstOaoNavigation) {
        const incompletedPages = this.flow.pages.filter(
          (t, index) =>
            (index < currentPageIndex &&
              t.visible &&
              !t.isActionPage &&
              (!t.helper.isPageComplete || !this.isExtradataCompleted(t, extraData)) &&
              t.id !== 'oao-membership') ||
            // If the page id is 'checkpoint' then check, Checkpoint page has passed. If not passed then make this page as incomplete
            (t.id === 'oao-application-checkpoint' && !hasApplicationCheckpointPass)
        );
        if (incompletedPages?.length > 0) {
          const navigateUrl = incompletedPages[0].path;
          console.error('Loopback - First Incomplete Page:', incompletedPages[0]);
          const extras: NavigationExtras = {};
          if (incompletedPages[0].config.preserveQueryParam) {
            extras.queryParams = this.route.snapshot.queryParams;
          }
          this.router.navigate([`${this.workflowService.currentFlow.id}/${navigateUrl}`], extras);
        }
      }

      if (isFirstOaoNavigation) {
        this.statefulService.wrap('ss:oao-first-time-navigation').value = false;
      }
    }
  }

  public cancelOAOBeneficiaryRemoval() {
    const pageIterationId = this.statefulService.wrap('ss:beneficiaryPageIterationId').valueNumber;
    const iterationId = this.statefulService.wrap('ss:beneficiaryRemoveCardIterationId').valueString;
    const cardId = this.statefulService.wrap('ss:beneficiaryRemoveCardId').valueString;
    if (cardId?.length) {
      const card = this.cardService.createCard(cardId, this.workflowService.currentFlow.id, +iterationId, pageIterationId);
      this.setFocusOAORemoveBeneficiary(card);
    }
    this.resetBeneficiarySSValues();
  }

  public agreeOAOBeneficiaryRemoval() {
    const removePath = this.statefulService.wrap('ss:beneficiaryRemovePath').valueString;
    const pageIterationId = this.statefulService.wrap('ss:beneficiaryPageIterationId').valueNumber;
    const iterationId = this.statefulService.wrap('ss:beneficiaryRemoveCardIterationId').valueString;
    const cardId = this.statefulService.wrap('ss:beneficiaryRemoveCardId').valueString;
    if (cardId?.length) {
      const card = this.cardService.createCard(cardId, this.workflowService.currentFlow.id, +iterationId, pageIterationId);
      card.fields.forEach((f) => {
        f.dirty = false;
        f.nestedFields.forEach((n) => (n.dirty = false));
      });
      StatefulValue.arrayRemove(removePath, +iterationId, () => {
        setTimeout(() => {
          this.calculateDefaultAllocation();
          // Reload the allocation card after new value updated in the bundle
          // OAO SetFocus
          this.setFocusOAOAddBeneficiary(card);
        }, 0);
      });
    } else {
      StatefulValue.arrayTruncate('api:oaoMemberInformation/beneficiary');
    }
    this.resetBeneficiarySSValues();
  }
  private resetBeneficiarySSValues() {
    this.statefulService.wrap('ss:beneficiaryRemovePath').value = '';
    this.statefulService.wrap('ss:beneficiaryPageIterationId').value = '';
    this.statefulService.wrap('ss:beneficiaryRemoveCardId').value = '';
    this.statefulService.wrap('ss:beneficiaryRemoveCardIterationId').value = '';
  }

  public setFocusOAOAddBeneficiary = (card: CardInstance): void => {
    if (card && card.id.includes('oao-beneficiary')) {
      const removeId = `remove-${card.id}`;
      const iterationId = card.iterationId;
      const removeBeneficiary = `remove-oao-beneficiary-${iterationId - 1}`;
      if (removeId.includes('remove-oao-beneficiary-0')) {
        document.getElementById('oao-add-beneficiary-0').focus();
      } else {
        document.getElementById(removeBeneficiary).focus();
      }
    }
  };

  public setFocusOAORemoveBeneficiary = (card: CardInstance): void => {
    if (card && card.id.includes('oao-beneficiary')) {
      const elementId = `remove-${card.id}`;
      if (elementId) {
        setTimeout(() => {
          const element = document.getElementById(elementId);
          if (element) {
            element.focus();
          }
        }, 100);
      }
    }
  };

  /** Moved from on-change-fuction service to remove its dependency in pagehelper */
  public calculateDefaultAllocation(): void {
    const maxPercent = 100;
    /** Set Primary & Contingent Array Size */
    let primarySize = 0;
    let contingentSize = 0;

    /** Set Default Addition Primary & Contingent Allocation Percentage (based on total beneficiary type) */
    let primaryValue = 0;
    let contingentValue = 0;

    const allSize = StatefulValue.arraySize('api:oaoMemberInformation/beneficiary');

    /** Check & set the Beneficiary type Count */
    for (let i = 0; i < allSize; i++) {
      const allocationType = this.statefulService.wrap(`api:oaoMemberInformation/beneficiary/$/allocationType`, i).valueStringOnly;
      if (allocationType === 'Primary') {
        primarySize += 1;
      } else if (allocationType === 'Contingent') {
        contingentSize += 1;
      }
    }

    if (primarySize > 0) {
      primaryValue = parseFloat((Number(maxPercent) / primarySize).toFixed(0));
    }

    if (contingentSize > 0) {
      contingentValue = parseFloat((Number(maxPercent) / contingentSize).toFixed(0));
    }

    this.setAllocationPercent(allSize, maxPercent, primaryValue, contingentValue);
  }

  /** Set Total Sum Value of Primary & Contingent Allocation Percentage */
  private setAllocationPercent(allSize: number, maxPercent: number, primaryValue: number, contingentValue: number) {
    let totalPrimaryValue = 0;
    let totalContingentValue = 0;
    // Loop Beneficiary Array & Setup the default Value
    for (let i = 0; i < allSize; i++) {
      const allocationType = this.statefulService.wrap(`api:oaoMemberInformation/beneficiary/$/allocationType`, i).valueStringOnly;

      if (allocationType === 'Primary') {
        if (totalPrimaryValue < maxPercent) {
          if (i === allSize - 1) {
            this.statefulService.wrap(`api:oaoMemberInformation/beneficiary/$/allocationPercent`, i).value =
              maxPercent - totalPrimaryValue + '%';
          } else {
            totalPrimaryValue += primaryValue;
            this.statefulService.wrap(`api:oaoMemberInformation/beneficiary/$/allocationPercent`, i).value = primaryValue + '%';
          }
        }
      } else if (allocationType === 'Contingent') {
        if (totalContingentValue < maxPercent) {
          if (i === allSize - 1) {
            this.statefulService.wrap(`api:oaoMemberInformation/beneficiary/$/allocationPercent`, i).value =
              maxPercent - totalContingentValue + '%';
          } else {
            totalContingentValue += contingentValue;
            this.statefulService.wrap(`api:oaoMemberInformation/beneficiary/$/allocationPercent`, i).value = contingentValue + '%';
          }
        }
      }
    }
  }

  public removeOaoApplicant(): void {
    const hasCoApplicant = this.statefulService.wrap('api:oaoMemberInformation/hasCoApplicant');
    const hasAdditionalCoApplicant = this.statefulService.wrap('api:oaoMemberInformation/hasAdditionalCoApplicant');
    if (this.page.id === 'oao-application-fields-additional-co-app') {
      hasAdditionalCoApplicant.value = false;
      this.deleteOaoApplicant('api:oaoMemberInformation/additionalCoApplicant');
      this.resetOaoPageNavigation('additional-co-app');
      this.router.navigateByUrl(`${this.flow.config.path}/oao-add-second-co-applicant`);
    }
    if (this.page.id === 'oao-application-fields-co-applicant') {
      if (!hasAdditionalCoApplicant.value) {
        hasCoApplicant.value = false;
        this.deleteOaoApplicant('api:oaoMemberInformation/coApplicant');
        this.resetOaoPageNavigation('co-applicant');
        this.router.navigateByUrl(`${this.flow.config.path}/oao-add-co-applicant`);
      } else {
        this.resetOaoPageNavigation('co-applicant');
        this.copyAndReplaceApplicantData('api:oaoMemberInformation/coApplicant', 'api:oaoMemberInformation/additionalCoApplicant');
        hasAdditionalCoApplicant.value = false;
        this.deleteOaoApplicant('api:oaoMemberInformation/additionalCoApplicant', false);
        this.resetOaoPageNavigation('additional-co-app');
        this.router.navigateByUrl(`${this.flow.config.path}/oao-add-second-co-applicant`);
      }
    }
    this.showDeleteJointApplicantModal = false;
  }

  public removeOaoApplicantAndContinue(): void {
    const hasCoApplicant = this.statefulService.wrap('api:oaoMemberInformation/hasCoApplicant');
    const hasAdditionalCoApplicant = this.statefulService.wrap('api:oaoMemberInformation/hasAdditionalCoApplicant');
    if (this.page.id === 'oao-application-fields-additional-co-app') {
      hasAdditionalCoApplicant.value = false;
      this.deleteOaoApplicant('api:oaoMemberInformation/additionalCoApplicant');
      this.resetOaoPageNavigation('additional-co-app');
    }
    if (this.page.id === 'oao-application-fields-co-applicant') {
      if (!hasAdditionalCoApplicant.value) {
        hasCoApplicant.value = false;
        this.deleteOaoApplicant('api:oaoMemberInformation/coApplicant');
        this.resetOaoPageNavigation('co-applicant');
      } else {
        this.resetOaoPageNavigation('co-applicant');
        this.copyAndReplaceApplicantData('api:oaoMemberInformation/coApplicant', 'api:oaoMemberInformation/additionalCoApplicant');
        hasAdditionalCoApplicant.value = false;
        this.deleteOaoApplicant('api:oaoMemberInformation/additionalCoApplicant', false);
        this.resetOaoPageNavigation('additional-co-app');
      }
    }
    this.router.navigateByUrl(`${this.flow.config.path}/accountsandservices`);
    this.showDeleteJointApplicantModal = false;
  }

  public setEligibilityFlow(): boolean {
    if (!(this.userService && this.userService.isAuthenticated)) {
      this.setOaoFlow();

      this.router.navigate(['/user/light-registration']);
      return true;
    }
    return false;
  }

  public setOaoFlow(): void {
    this.userService.flowType = 'OAO';
  }

  public oaoAppDeclined(): boolean {
    const decisionStatus = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationStatus').value;
    if (decisionStatus && decisionStatus.toString().toLowerCase() === DecisionStatus.Declined) {
      return true;
    }
    return false;
  }

  /** Check ready to submit OAO application */
  public readyToSubmitOAO(): boolean {
    /* Check all te pages are completed. (we are checking this logic becze Identity verification
     * and Accounts & services page data not yet added to bundle. Once it is added to the bundle then
     * we can remove this incomlete page check & can have only readyToSubmit property alone) */
    // eslint-disable-next-line eqeqeq
    const incompletedPages = this.flow.pages.filter((t) => t.visible && !t.helper.isPageComplete && t.id != 'oao-application-checkpoint');
    return incompletedPages.length === 0;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private isExtradataCompleted({ id, config }: PageInstance, extraData: string): boolean {
    if (!extraData) {
      return false;
    }

    const extraDataObj = JSON.parse(extraData);
    return !(
      !extraDataObj[id] ||
      !extraDataObj[id].isVisited ||
      !extraDataObj[id].state ||
      extraDataObj[id].state.toLowerCase() !== 'completed'
    );
  }

  public resetOaoPageNavigation(pageIdSuffix: string): void {
    this.flow.pages
      .filter((page) => page.id.endsWith(pageIdSuffix))
      .forEach((page) => {
        // this.dynamicPageService.clearPageState(page);
        page.helper.touchedFields.forEach((field) => {
          field.dirty = false;
        });
      });
  }

  public clearOAOApplicationDataForManual(): void {
    const option = this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/autoFillOption').valueStringOnly;
    const previousValue = this.statefulService.wrap('mem:previous-autofill-option').valueStringOnly;
    if (option && option.toLowerCase() === 'manual' && option.toLowerCase() !== previousValue) {
      this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/firstName').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/lastName').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/dateOfBirth').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/homePhone').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/ssn').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/mobileNumber').value = '';
      StatefulValue.arrayTruncate('api:oaoMemberInformation/primaryApplicant/employments');
      StatefulValue.arrayTruncate('api:oaoMemberInformation/primaryApplicant/primaryAddress');
      this.statefulService.wrap('ss:oao-primary-applicant-prefill-alert-success').value = null;

      // adding value in APi
      this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/autoFillOption').value = 'Manual';
      this.statefulService.wrap('api:oaoMemberInformation/iSPrefilledByMobile').value = false;
      this.statefulService.wrap('api:oaoMemberInformation/isPrefilled').value = false;
    }

    const coApplicantOption = this.statefulService.wrap('api:oaoMemberInformation/coApplicant/autoFillOption').valueStringOnly;
    const coApplicantPreviousValue = this.statefulService.wrap('mem:previous-autofill-option').valueStringOnly;
    if (coApplicantOption && coApplicantOption.toLowerCase() === 'manual' && coApplicantOption.toLowerCase() !== coApplicantPreviousValue) {
      // coApplicant
      this.statefulService.wrap('api:oaoMemberInformation/coApplicant/firstName').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/coApplicant/lastName').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/coApplicant/dateOfBirth').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/coApplicant/homePhone').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/coApplicant/ssn').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/coApplicant/mobileNumber').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/coApplicant/email').value = '';
      StatefulValue.arrayTruncate('api:oaoMemberInformation/coApplicant/employments');
      StatefulValue.arrayTruncate('api:oaoMemberInformation/coApplicant/primaryAddress');
      this.statefulService.wrap('ss:oao-primary-co-applicant-prefill-alert-success').value = null;

      // adding value in APi
      this.statefulService.wrap('api:oaoMemberInformation/coApplicant/autoFillOption').value = 'Manual';
      this.statefulService.wrap('api:oaoMemberInformation/iSPrefilledByMobile').value = false;
      this.statefulService.wrap('api:oaoMemberInformation/isPrefilled').value = false;
    }

    const additionalCoApplicantOption = this.statefulService.wrap(
      'api:oaoMemberInformation/additionalCoApplicant/autoFillOption'
    ).valueStringOnly;
    const additionalCoApplicantPreviousValue = this.statefulService.wrap('mem:previous-autofill-option').valueStringOnly;
    if (
      additionalCoApplicantOption &&
      additionalCoApplicantOption.toLowerCase() === 'manual' &&
      additionalCoApplicantOption.toLowerCase() !== additionalCoApplicantPreviousValue
    ) {
      // coApplicant
      this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/firstName').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/lastName').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/dateOfBirth').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/homePhone').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/ssn').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/mobileNumber').value = '';
      this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/email').value = '';
      StatefulValue.arrayTruncate('api:oaoMemberInformation/additionalCoApplicant/employments');
      StatefulValue.arrayTruncate('api:oaoMemberInformation/additionalCoApplicant/primaryAddress');
      this.statefulService.wrap('ss:oao-primary-additional-co-applicant-prefill-alert-success').value = null;

      // adding value in APi
      this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/autoFillOption').value = 'Manual';
      this.statefulService.wrap('api:oaoMemberInformation/iSPrefilledByMobile').value = false;
      this.statefulService.wrap('api:oaoMemberInformation/isPrefilled').value = false;
    }

    this.statefulService.wrap('mem:previous-autofill-option').value = option ? option.toLowerCase() : '';
  }

  /**
   * Deletes an applicant
   * @param storageIdPrefix The storage id for the applicant to be deleted
   */
  public deleteOaoApplicant(storageIdPrefix: string, shouldDeleteArrayElements = true): void {
    this.statefulService.wrap(storageIdPrefix + '/firstName').value = null;
    this.statefulService.wrap(storageIdPrefix + '/middleName').value = null;
    this.statefulService.wrap(storageIdPrefix + '/lastName').value = null;
    this.statefulService.wrap(storageIdPrefix + '/mothersMaidenName').value = null;
    this.statefulService.wrap(storageIdPrefix + '/dateOfBirth').value = '';
    this.statefulService.wrap(storageIdPrefix + '/homePhone').value = null;
    this.statefulService.wrap(storageIdPrefix + '/email').value = null;
    this.statefulService.wrap(storageIdPrefix + '/ssn').value = null;
    this.statefulService.wrap(storageIdPrefix + '/mobileNumber').value = null;
    this.statefulService.wrap(storageIdPrefix + '/citizenshipStatus').value = null;
    this.statefulService.wrap(storageIdPrefix + '/idType').value = null;
    this.statefulService.wrap(storageIdPrefix + '/idIssuedBy').value = null;
    this.statefulService.wrap(storageIdPrefix + '/idNumber').value = null;
    this.statefulService.wrap(storageIdPrefix + '/idIssuedDate').value = null;
    this.statefulService.wrap(storageIdPrefix + '/idExpirationDate').value = null;
    this.statefulService.wrap(storageIdPrefix + '/autoFillOption').value = null;
    this.statefulService.wrap(storageIdPrefix + '/autoFillStatus').value = null;
    this.statefulService.wrap(storageIdPrefix + '/autoFillDateOfBirthValid').value = false;
    this.statefulService.wrap(storageIdPrefix + '/enableAutoFillFirstName').value = true;
    this.statefulService.wrap(storageIdPrefix + '/enableAutoFillMobileNumber').value = true;
    this.statefulService.wrap(storageIdPrefix + '/enableAutoFillSSN').value = true;
    this.statefulService.wrap(storageIdPrefix + '/isPrefilled').value = null;
    this.statefulService.wrap(storageIdPrefix + '/sameAsPrimaryAddress').value = null;
    this.statefulService.wrap(storageIdPrefix + '/consent/isAccepted').value = null;
    this.statefulService.wrap(storageIdPrefix + '/consent/agreedDate').value = null;
    this.statefulService.wrap(storageIdPrefix + '/consentTCPA/isAccepted').value = null;
    this.statefulService.wrap(storageIdPrefix + '/consentTCPA/agreedDate').value = null;
    this.clearAccountsAndServices(storageIdPrefix);
    this.clearAdditionalConsents(storageIdPrefix);
    if (shouldDeleteArrayElements) {
      StatefulValue.arrayTruncate(storageIdPrefix + '/employments');
      StatefulValue.arrayTruncate(storageIdPrefix + '/primaryAddress');
      StatefulValue.arrayTruncate(storageIdPrefix + '/additionalConsents');
    }
    const index = storageIdPrefix.includes('additionalCoApplicant') ? 2 : 1;
    this.removeRequestedDocByApplicant(index);
  }

  /**
   * Clears the additionalConsents array
   * @param storageIdPrefix
   */
  private clearAdditionalConsents(storageIdPrefix: string): void {
    const rootPath = storageIdPrefix + '/additionalConsents';
    const additionalConsents = this.stateManagementService.getValue<CapturedConsent[]>(rootPath) || [];
    additionalConsents.forEach((t, index) => {
      this.statefulService.wrap(`${rootPath}/${index}/isAccepted`).value = null;
      this.statefulService.wrap(`${rootPath}/${index}/acceptanceText`).value = null;
      this.statefulService.wrap(`${rootPath}/${index}/acknowledgeId`).value = null;
      this.statefulService.wrap(`${rootPath}/${index}/agreedDate`).value = null;
      this.statefulService.wrap(`${rootPath}/${index}/acceptanceSnapshotReferenceId`).value = null;
    });
  }

  /**
   * Clears the accountsAndServices object and all of its contents
   * @param storageIdPrefix
   */
  private clearAccountsAndServices(storageIdPrefix: string): void {
    const path = storageIdPrefix.includes('oaoMemberInformation/')
      ? storageIdPrefix.replace(/(oaoMemberInformation\/)/, '$1accountsAndServices/')
      : storageIdPrefix + '/accountsAndServices';

    this.statefulService.wrap(path + '/firstName').value = null;
    this.statefulService.wrap(path + '/lastName').value = null;
    this.l360Service.cachedAccountAndService = null;
  }

  /** Method to remove requested doc details by applicant
   * @param applicantIndex applicantIndex is 1 for first co-app and 2 for second co-app
   */
  public removeRequestedDocByApplicant(applicantIndex: number): void {
    this.requestedDocById('api:oaoMemberInformation/requestedDocuments/rdc1', applicantIndex);
    this.requestedDocById('api:oaoMemberInformation/requestedDocuments/rdc2', applicantIndex);
    this.requestedDocById('api:oaoMemberInformation/requestedDocuments/rdc3', applicantIndex);
    this.requestedDocById('api:oaoMemberInformation/requestedDocuments/rdc4', applicantIndex);
    this.requestedDocById('api:oaoMemberInformation/requestedDocuments/rdc5', applicantIndex);
  }

  private requestedDocById(docStorageId: string, applicantIndex: number): void {
    const data = this.statefulService.wrap(docStorageId).valueStringOnly;
    const parsedData = data?.length > 0 ? JSON.parse(data) : null;
    if (parsedData?.documents?.length) {
      parsedData.documents = parsedData.documents.filter((t) => t.uploadedBy !== applicantIndex);
      this.statefulService.wrap(docStorageId).value = JSON.stringify(parsedData);
    }
  }

  /**
   * Replaces one applicant with the copy of another applicant
   * @param storageIdPrefix The storage id for the applicant that will be replaced
   * @param copyStorageIdPrefix The storage id for the applicant that will be copied
   */
  private copyAndReplaceApplicantData(storageIdPrefix: string, copyStorageIdPrefix: string): void {
    this.statefulService.wrap(storageIdPrefix + '/firstName').value = this.statefulService.wrap(copyStorageIdPrefix + '/firstName').value;
    this.statefulService.wrap(storageIdPrefix + '/middleName').value = this.statefulService.wrap(copyStorageIdPrefix + '/middleName').value;
    this.statefulService.wrap(storageIdPrefix + '/lastName').value = this.statefulService.wrap(copyStorageIdPrefix + '/lastName').value;
    this.statefulService.wrap(storageIdPrefix + '/mothersMaidenName').value = this.statefulService.wrap(
      copyStorageIdPrefix + '/mothersMaidenName'
    ).value;
    this.statefulService.wrap(storageIdPrefix + '/dateOfBirth').value = this.statefulService.wrap(
      copyStorageIdPrefix + '/dateOfBirth'
    ).value;
    this.statefulService.wrap(storageIdPrefix + '/homePhone').value = this.statefulService.wrap(copyStorageIdPrefix + '/homePhone').value;
    this.statefulService.wrap(storageIdPrefix + '/email').value = this.statefulService.wrap(copyStorageIdPrefix + '/email').value;
    this.statefulService.wrap(storageIdPrefix + '/ssn').value = this.statefulService.wrap(copyStorageIdPrefix + '/ssn').value;
    this.statefulService.wrap(storageIdPrefix + '/mobileNumber').value = this.statefulService.wrap(
      copyStorageIdPrefix + '/mobileNumber'
    ).value;
    this.statefulService.wrap(storageIdPrefix + '/idType').value = this.statefulService.wrap(copyStorageIdPrefix + '/idType').value;
    this.statefulService.wrap(storageIdPrefix + '/idIssuedBy').value = this.statefulService.wrap(copyStorageIdPrefix + '/idIssuedBy').value;
    this.statefulService.wrap(storageIdPrefix + '/idNumber').value = this.statefulService.wrap(copyStorageIdPrefix + '/idNumber').value;
    this.statefulService.wrap(storageIdPrefix + '/idIssuedDate').value = this.statefulService.wrap(
      copyStorageIdPrefix + '/idIssuedDate'
    ).value;
    this.statefulService.wrap(storageIdPrefix + '/idExpirationDate').value = this.statefulService.wrap(
      copyStorageIdPrefix + '/idExpirationDate'
    ).value;
    this.copyAndReplaceApplicantResidenceData(storageIdPrefix, copyStorageIdPrefix);
    this.copyAndReplaceApplicantEmploymentData(storageIdPrefix, copyStorageIdPrefix);
  }

  /**
   * Replaces one applicant residence with the copy of another
   * @param storageIdPrefix The storage id for the applicant that will be replaced
   * @param copyStorageIdPrefix The storage id for the applicant that will be copied
   */
  private copyAndReplaceApplicantResidenceData(storageIdPrefix: string, copyStorageIdPrefix: string): void {
    StatefulValue.arrayTruncate(storageIdPrefix + '/primaryAddress', () => {
      const arraySize = StatefulValue.arraySize(copyStorageIdPrefix + '/primaryAddress');
      for (let i = 0; i < arraySize; i++) {
        StatefulValue.arrayAdd(storageIdPrefix + '/primaryAddress', () => {
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/addressLine1', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/addressLine1',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/addressLine2', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/addressLine2',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/addressId', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/addressId',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/addressType', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/addressType',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/city', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/city',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/years', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/years',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/months', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/months',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/usResident', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/usResident',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/residenceBasisType', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/residenceBasisType',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/rentAmount', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/rentAmount',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/residencyType', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/residencyType',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/state', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/state',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/zipCode', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/zipCode',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/province', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/province',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/country', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/country',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/postCode', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/postCode',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/housingType', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/housingType',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/primaryAddress/$/monthlyPayment', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/primaryAddress/$/monthlyPayment',
            i
          ).value;
          StatefulValue.arrayTruncate(copyStorageIdPrefix + '/primaryAddress');
        });
      }
    });
  }

  /**
   * Replaces one applicant employment with the copy of another
   * @param storageIdPrefix The storage id for the applicant that will be replaced
   * @param copyStorageIdPrefix The storage id for the applicant that will be copied
   */
  private copyAndReplaceApplicantEmploymentData(storageIdPrefix: string, copyStorageIdPrefix: string): void {
    StatefulValue.arrayTruncate(storageIdPrefix + '/employments', () => {
      const arraySize = StatefulValue.arraySize(copyStorageIdPrefix + '/employments');
      for (let i = 0; i < arraySize; i++) {
        StatefulValue.arrayAdd(storageIdPrefix + '/employments', () => {
          this.statefulService.wrap(storageIdPrefix + '/employments/$/jobTitle', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/employments/$/jobTitle',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/employments/$/employmentStatus', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/employments/$/employmentStatus',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/employments/$/startDate', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/employments/$/startDate',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/employments/$/years', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/employments/$/years',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/employments/$/months', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/employments/$/months',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/employments/$/income', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/employments/$/income',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/employments/$/frequency', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/employments/$/frequency',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/employments/$/averageHoursWorkedPerWeek', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/employments/$/averageHoursWorkedPerWeek',
            i
          ).value;
          this.statefulService.wrap(storageIdPrefix + '/employments/$/retiredIncomeSource', i).value = this.statefulService.wrap(
            copyStorageIdPrefix + '/employments/$/retiredIncomeSource',
            i
          ).value;
          StatefulValue.arrayTruncate(copyStorageIdPrefix + '/employments');
        });
      }
    });
  }

  public savePreciseIDAnswers(): boolean {
    const hasPIDAnswer = this.statefulService.wrap('ss:oaoMemberInformation/identityVerificationQuestions').value;
    const isEmptyPID = this.statefulService.wrap('ss:oaoMemberInformation/isEmptyPIDReport').value;
    if (hasPIDAnswer || isEmptyPID) {
      this.submitPIDAnswers();
      return true;
    } else {
      // confirm: this is valid for alloy
      const nextPage = this.workflowService.findNextPage(this.page);
      if (nextPage) {
        this.router.navigateByUrl(`/${this.workflowService.currentFlow.id}/${nextPage.path}`);
      } else {
        this.router.navigateByUrl(`/${this.workflowService.currentFlow.id}/${APPLICATION_REVIEW_PAGE_ID}`);
      }
      return false;
    }
  }

  public submitPIDAnswers(): void {
    /** Logics to handle save button disbled */
    this.statefulService.wrap('mem:show-card-spinner').value = true;
    this.page.actionButtonIsLoading = true;
    /** Calling Api to submit answers */
    this.l360Service.savePreciseIDAnswers().subscribe(
      (result) => {
        if (result && result.data) {
          /** Allowing User to next page if final decison is done from L360) */
          if ((result.data.preciseIdFinalDecision && result.data.preciseIdFinalScore) || result.data.isEmptyPIDReport) {
            if (result.data.isPIDCompleted) {
              this.bundleHelperService.refreshBundle(() => {
                this.statefulService.wrap('mem:oao-identity-verification-question').value = 'true';
                /** Updating the bundle to DDMS */
                this.applicationApiService.updateBundleDataToDDMS();
                /** Hiding spinner and moving next page */
                this.statefulService.wrap('mem:show-card-spinner').value = false;
                this.page.actionButtonIsLoading = false;
                // confirm: this is valid for alloy
                const nextPage = this.workflowService.findNextPage(this.page);
                if (nextPage) {
                  this.router.navigateByUrl(`/${this.workflowService.currentFlow.id}/${nextPage.path}`);
                } else {
                  this.router.navigateByUrl(`/${this.workflowService.currentFlow.id}/${APPLICATION_REVIEW_PAGE_ID}`);
                }
                return;
              });
            } else {
              this.bundleHelperService.refreshBundle(() => {
                this.applicationApiService.updateBundleDataToDDMS();
                this.statefulService.wrap('ss:oaoMemberInformation/identityVerificationQuestions').value = '';
                this.statefulService.wrap('mem:show-card-spinner').value = false;
                this.page.actionButtonIsLoading = false;
                /** Triggers an subject to update the new questions on verification component */
                this.l360Service.submissionPreciseIdAnswers.next(false);
                return;
              });
            }
            /** If decision is not done, we will get another set of question */
          } else if (result.data.oowQuestions) {
            /** Logic to update the exsisting questions to current set of questions  */
            const previousResponse: PreciseIDQuestionResponse = JSON.parse(
              this.statefulService.wrap('ss:oaoMemberInformation/identityVerificationQuestions').valueStringOnly
            );
            previousResponse.oowQuestion = result.data.oowQuestions;
            this.statefulService.wrap('ss:oaoMemberInformation/identityVerificationQuestions').value = JSON.stringify(previousResponse);
            this.statefulService.wrap('mem:show-card-spinner').value = false;
            this.page.actionButtonIsLoading = false;
            /** Triggers an subject to update the new questions on verification component */
            this.l360Service.submissionPreciseIdAnswers.next(true);
            return;
          } else {
            // TODO Operations for any error
            this.page.actionButtonIsLoading = false;
            this.statefulService.wrap('mem:show-card-spinner').value = false;
          }
        } else {
          // TODO Operatiosns for any error
          this.statefulService.wrap('mem:show-card-spinner').value = false;
          this.page.actionButtonIsLoading = false;
        }
      },
      () => {
        /* TODO Operatiosns for any error we move to next page */
        this.statefulService.wrap('mem:show-card-spinner').value = false;
        this.page.actionButtonIsLoading = false;
        this.statefulService.wrap('mem:oao-identity-verification-question').value = 'true';
        // confirm: this is valid for alloy
        const nextPage = this.workflowService.findNextPage(this.page);
        if (nextPage) {
          this.router.navigateByUrl(`/${this.workflowService.currentFlow.id}/${nextPage.path}`);
        } else {
          this.router.navigateByUrl(`/${this.workflowService.currentFlow.id}/${APPLICATION_REVIEW_PAGE_ID}`);
        }
      }
    );
    /**
     * prepares Previous text for OAO workflow
     */
  }
  public getPreviousTextForOAO(): string {
    return this.page.config.previous.toString();
  }

  public ValidateAllOAOPage(): void {
    if (this.flow.id.toLowerCase().indexOf(OAO) > -1 && this.page.id === 'oao-membership') {
      /* below code Validate all pages if membership changed */
      this.statefulService.wrap('mem:ValidateAllPages').afterPatch(() => {
        this.flow.pages.forEach((page) => {
          page.cards.forEach((c) => {
            c.fields.forEach((f) => f.revalidate());
          });
          // this.dynamicPageService.updateNavState(page, page.helper.isPageTouched);
        });
      });
    }
  }

  public SetOAOMembership(membershipID: string, flowId: string): void {
    const enhancedProductSelectionEnabled = this.lenderService.lenderData.enableEnhancedProductSelection;
    const field = enhancedProductSelectionEnabled
      ? this.fieldService.createField('oao-membership-list', flowId)
      : this.fieldService.createField('oao-membership-list-dropdown', flowId);
    // eslint-disable-next-line max-len
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/dot-notation
    if (field.config.payload && field.config.payload['dropdownLists'] && (field.config.payload['dropdownLists'] as any).length > 0) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      const memberships = JSON.parse(field.config.payload['dropdownLists'] as string);
      // eslint-disable-next-line eqeqeq
      if (!memberships || memberships.length == 0) {
        return;
      }
      const member = memberships.filter((i) => i.value.toString() === membershipID);
      this.setStateValue(member);
    }
  }

  public setStateValue(memberships) {
    if (memberships && memberships.length > 0) {
      this.statefulService.wrap('api:oaoMemberInformation/membership/membershipID').value = memberships[0].value
        ? memberships[0].value
        : '';
      this.statefulService.wrap('api:oaoMemberInformation/membership/membershipName').value = memberships[0].label
        ? memberships[0].label
        : '';
      this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeID').value = memberships[0].membershipTypeID
        ? memberships[0].membershipTypeID
        : '';
      this.statefulService.wrap('api:oaoMemberInformation/membership/minAge').value = this.getMinMaxAge(
        memberships[0].minAge,
        memberships[0].isYouth,
        13
      );
      this.statefulService.wrap('api:oaoMemberInformation/membership/maxAge').value = this.getMinMaxAge(
        memberships[0].maxAge,
        memberships[0].isYouth,
        18
      );
      this.statefulService.wrap('api:oaoMemberInformation/membership/membershipFee').value = memberships[0].membershipFee;
      this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').value = memberships[0].enumId
        ? memberships[0].enumId
        : null;
      this.statefulService.wrap('api:oaoMemberInformation/membership/isYouth').value = memberships[0].isYouth;
      this.statefulService.wrap('api:oaoMemberInformation/membership/redirectURL').value = memberships[0].redirectURL;
      this.statefulService.wrap(`api:oaoMemberInformation/hasCoApplicant`).value = memberships[0].isYouth ? memberships[0].isYouth : null;
      this.statefulService.wrap('api:oaoMemberInformation/showPreferredbankbranches').value = true;
      this.statefulService.wrap('api:oaoMemberInformation/showAdditionalQuestions').value = true;
      this.statefulService.wrap('api:oaoMemberInformation/membership/organizationID').value = Number(memberships[0].organizationID);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private getMinMaxAge(value: string, isYouth: boolean, defaultValue): number | string {
    return value ? value : null;
  }

  public setBusinessMembership(): void {
    const isPrefilled = this.statefulService.wrap(OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_ISPREFILLED).value;
    if (isPrefilled !== true) {
      this.statefulService.wrap(OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_EMAIL).value =
        this.statefulService.wrap(OAO_PRIMARY_APPLICANT_EMAIL).value;
      this.statefulService.wrap(OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_MOBILENUMBER).value =
        this.statefulService.wrap(OAO_PRIMARY_APPLICANT_MOBILENUMBER).value;
    }
  }

  submitCompareAndFraud(): void {
    const objOAOComapreAndFraudApplicant = this.instatouchService.makeOAOComapreAndFraudAPICall();
    if (objOAOComapreAndFraudApplicant && objOAOComapreAndFraudApplicant.autofillPicked) {
      const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
      objOAOComapreAndFraudApplicant.applicants.forEach((applicant) => {
        applicant.membershipTypeName = membershipTypeName;
      });
      this.instatouchService.getCompareandFraudResponse(objOAOComapreAndFraudApplicant.applicants).subscribe((response) => {
        if (response.isSuccess) {
          this.bundleHelperService.refreshBundleAsync();
        }
      });
    }
  }

  /** get notification status */
  getNotificatoinStatus(): void {
    this.applicationApiService.getNotificatoinStatus((response) => {
      if (response.data) {
        this.userService.isPostAppNotificationActive = response?.data?.isPostAppNotificationActive;
        this.userService.postAppNotificationMessage = response?.data?.postAppNotificationMessage;
        this.userService.postAppStatusMessage = response?.data?.postAppStatusMessage;
      }
    });
  }

  /** ResetRequestedDoc */
  public resetRequestedDoc = (): void => {
    const isNewLoan = this.statefulService.wrap('ss:oao-fresh-loan').value;
    if (!(isNewLoan === 'true' || isNewLoan === true)) {
      return;
    }

    this.flow.pages.forEach((currentPage: PageInstance) => {
      currentPage.cards.forEach((c) => {
        c.fields.forEach((f: FieldInstance) => {
          if (f.config.storageId?.toLowerCase().includes('oaomemberinformation/requesteddocuments')) {
            f.data.value = '';
            this.statefulService.wrap('ss:oao-fresh-loan').value = false;
          }
        });
      });
    });
  };

  /**
   * Set OAO Lender Settings. Set PID enabled for the lender or not
   */
  public setOAOLenderSettings(): void {
    this.l360Service.setOAOLenderSettings();
  }
}
